import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { NumericFormat } from "react-number-format";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import PrintButton from "../PrintButton";
import DownloadButton from "../DownloadButton";
import api3001 from "../../services/api3001";
import api3004 from "../../services/api3004";
import ClientSearch from "../ClientSearch";

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  font-family: "Segoe UI", sans-serif;


  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem;
  }
`;

const SectionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const Section = styled.div`
  flex: 1;
  min-width: 300px;

  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  font-weight: bold;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const ButtonBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Button = styled.button`
  margin: 5px;
  padding: 10px 12px;
  background-color: #007bff;
  font-size: 16px;
  cursor: pointer;
  border-radius: 8px;
  color: #007bff;
  background-color: transparent;
  transition: background-color 0.3s ease, transform 0.2s ease, border-color 0.3s ease, color 0.3s ease;
  text-align: center;
  display: inline-block;
  border: 1px solid #007bff;
  

  &:hover {
    background-color: #007bff;
    color: white;
  }

  &:active {
    color: white;
    border-color: #007bff;
    transform: scale(0.98);
    background-color: #0056b3;
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
  }

  @media (max-width: 480px) {
    padding: 4px 8px;
    min-height: 7vh;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StyledNumeric = styled(NumericFormat)`
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 1rem;
`;

const Calculations = styled.div`
  margin-top: 2rem;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const CalcRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CalcLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
`;

const CalcValue = styled.span`
  font-size: 1rem;
  font-weight: 600;
  background-color: #eef2f7;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  min-width: 120px;
  text-align: right;
  color: #111827;
`;

const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid ${props => props.error ? "#ef4444" : "#ccc"};
  border-radius: 8px;
  font-size: 1rem;
`;

const Select = styled.select`
  padding: 0.6rem;
  border: 1px solid ${props => props.error ? "#ef4444" : "#ccc"};
  border-radius: 8px;
  font-size: 1rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;

  span{
    color:#ef4444;
    margin-left:4px;
  }
`;

const FormOrcamento = ({ modo, id, onSuccess }) => {
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const [camposFaltando, setCamposFaltando] = useState([]);

  const [onibusLista, setOnibusLista] = useState([]);

  const [totais, setTotais] = useState({
    valorOrcado: 0,
    custoTotal: 0,
    receita: 0,
    margem: 0,
  });

  const [formData, setFormData] = useState({
    cliente_nome: "",
    origem: "",
    destino: "",
    data_inicio: "",
    data_fim: "",
    km_total: "",
    valor_km: "",
    pouso: true,
    pagador_pouso: "",
    valor_dia_pouso: "",
    pagador_alimentacao: "",
    valor_dia_alimentacao: "",
    pagador_estacionamento: "",
    valor_dia_estacionamento: "",
    consumo_medio: "",
    valor_litro_combustivel: "",
    valor_pedagio: "",
    perc_garagem: "",
    perc_manutencao: "",
    criado_por: userId,
    perc_folha_salarial: "",
    perc_comissao_motorista: "",
    caixa_agua: "",
    data_pagamento: "",
    data_sinal: "",
    km_embarque: "",
    horario_saida: "",
    horario_retorno: "",
    status_pagamento: "",
    status_viagem: "",
    motorista: "",
    onibus_id: "",
    nome_terceiro: "",
    diaria: "",
    km_total_realizado: "",
  });

  let criado_em = "";

  useEffect(() => {

    if (!id) return;

    api3001
      .get(`/api/orcamento/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((response) => {

        const data = response.data;

        if (modo === "editar") {

          setFormData({
            cliente_nome: data.cliente_nome || "",
            origem: data.origem || "",
            destino: data.destino || "",
            data_inicio: formatDate(data.data_inicio),
            data_fim: formatDate(data.data_fim),
            km_total: data.km_total || "",
            valor_km: data.valor_km || "",
            pouso: !!data.pouso,
            pagador_pouso: data.pagador_pouso || "",
            valor_dia_pouso: data.valor_dia_pouso || "",
            pagador_alimentacao: data.pagador_alimentacao || "",
            valor_dia_alimentacao: data.valor_dia_alimentacao || "",
            pagador_estacionamento: data.pagador_estacionamento || "",
            valor_dia_estacionamento: data.valor_dia_estacionamento || "",
            consumo_medio: data.consumo_medio || "",
            valor_litro_combustivel: data.valor_litro_combustivel || "",
            valor_pedagio: data.valor_pedagio || "",
            perc_garagem: data.perc_garagem || "",
            perc_manutencao: data.perc_manutencao || "",
            criado_por: data.criado_por || userId,
            perc_folha_salarial: data.perc_folha_salarial || "",
            perc_comissao_motorista: data.perc_comissao_motorista || "",
            caixa_agua: data.caixa_agua || "",
            data_pagamento: formatDate(data.data_pagamento),
            data_sinal: formatDate(data.data_sinal),
            km_embarque: data.km_embarque || "",
            horario_saida: data.horario_saida || "",
            horario_retorno: data.horario_retorno || "",
            status_pagamento: data.status_pagamento || "",
            status_viagem: data.status_viagem || "",
            motorista: data.motorista || "",
            onibus_id: data.onibus_id || "",
            nome_terceiro: data.nome_terceiro || "",
            diaria: data.diaria || "",
            km_total_realizado: data.km_total_realizado || ""
          });

        } else {

          const {
            id,
            criado_por,
            criado_em,
            atualizado_em,
            status_pagamento,
            status_viagem,
            motorista,
            onibus_id,
            data_sinal,
            data_pagamento,
            data_fim,
            data_inicio,
            nome_terceiro,
            km_total_realizado,
            ...dadosLimpos
          } = data;

          setFormData(prev => ({
            ...prev,
            ...dadosLimpos,
            status_viagem: "orcamento",
            status_pagamento: "pendente",
            motorista: "",
            onibus_id: "",
            nome_terceiro: "",
            km_total_realizado: "",
            data_sinal: "",
            data_pagamento: "",
            data_fim: "",
            data_inicio: "",
          }));

        }

      })
      .catch((error) => {
        console.error("Erro ao buscar orçamento:", error);
      });

  }, [modo, id]);

  useEffect(() => {
    const totaisCalculados = calcularTotaisOrcamento(formData);
    setTotais(totaisCalculados);
  }, [formData]);

  useEffect(() => {

  const carregarOnibus = async () => {

    try {

      const res = await api3004.get("/api/onibus");

      setOnibusLista(res.data || []);

    } catch (err) {

      console.error("Erro ao carregar ônibus", err);

    }

  };

  carregarOnibus();

}, []);

// Função auxiliar segura para converter string com vírgula ou ponto para número
const toNumber = (valor) => {
  if (typeof valor === 'string') {
    const clean = valor.trim().replace(/[^\d.,-]/g, '');

    // Formato: "1.234,56" → "1234.56"
    if (clean.match(/\d+\.\d{3},\d{2}$/)) {
      return parseFloat(clean.replace(/\./g, '').replace(',', '.')) || 0;
    }

    // Formato: "1234,56" → "1234.56"
    if (clean.includes(',') && !clean.includes('.')) {
      return parseFloat(clean.replace(',', '.')) || 0;
    }

    // Já está em formato padrão JS: "1234.56"
    return parseFloat(clean) || 0;
  }

  return typeof valor === 'number' ? valor : 0;
};


const calcularTotaisOrcamento = (dados) => {
  const dataInicio = new Date(dados.data_inicio);
  const dataFim = new Date(dados.data_fim);
  
  const diasViagem = Math.max(1, Math.ceil((dataFim - dataInicio) / (1000 * 60 * 60 * 24)));

  const diariaTotal = diasViagem * toNumber(dados.diaria);

  const kmRealizado = toNumber(dados.km_total_realizado);
  const kmtotal = (kmRealizado === 0 ? toNumber(dados.km_total) : kmRealizado) + toNumber(dados.km_embarque);
  const custoKm = kmtotal * toNumber(dados.valor_km);

  let custoPouso = 0;
  if (dados.pouso && dados.pagador_pouso === 'esportiva') {
    custoPouso = toNumber(dados.valor_dia_pouso) * diasViagem;
  }

  const custoAlimentacao = dados.pagador_alimentacao === 'esportiva'
    ? toNumber(dados.valor_dia_alimentacao) * diasViagem
    : 0;

  const custoEstacionamento = dados.pagador_estacionamento === 'esportiva'
    ? toNumber(dados.valor_dia_estacionamento) * diasViagem
    : 0;

  const valorOrcado = custoKm + custoPouso + custoAlimentacao + custoEstacionamento + diariaTotal;

  const consumoMedio = toNumber(dados.consumo_medio);
  const valorLitro = toNumber(dados.valor_litro_combustivel);
  const custoCombustivel = (kmtotal > 0 && consumoMedio > 0)
    ? (kmtotal / consumoMedio) * valorLitro
    : 0;

  const custoBase = custoCombustivel
    + toNumber(dados.valor_pedagio)
    + toNumber(dados.caixa_agua);

  const custoTotal = custoBase
    + (valorOrcado * toNumber(dados.perc_garagem) / 100)
    + (valorOrcado * toNumber(dados.perc_manutencao) / 100)
    + (valorOrcado * toNumber(dados.perc_folha_salarial) / 100)
    + (valorOrcado * toNumber(dados.perc_comissao_motorista) / 100);

  const receita = valorOrcado - custoTotal;
  const margem = valorOrcado > 0 ? receita / valorOrcado : 0;

  return {
    valorOrcado,
    custoTotal,
    receita,
    margem
  };
};


  const limits = {
    cliente_nome: 100,
    origem: 100,
    destino: 100,
    motorista: 100,
    nome_terceiro: 100,
    caixa_agua: 100,
  };

  const numberLimits = {
    km_total: 99999.99,
    valor_km: 99999999.99,
    consumo_medio: 999.99,
    perc_garagem: 100,
    perc_manutencao: 100,
    perc_folha_salarial: 100,
    perc_comissao_motorista: 100,
  };

  const handleChange = (name, value) => {
    // limite texto
    if (limits[name] && value.length > limits[name]) return;

    // limite número
    if (numberLimits[name]) {
      const num = parseFloat(value);
      if (!isNaN(num) && num > numberLimits[name]) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectClient = (client) => {
    setFormData((prev) => ({
      ...prev,
      cliente_nome: client.nome,
    }));
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const d = new Date(isoDate);
    return d.toISOString().split("T")[0];
  };

  const handleSubmit = async () => {

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Conexão expirada",
        text: "Por favor, faça login novamente.",
        confirmButtonText: "OK"
      }).then(() => {
        navigate("/");
      });
      return;
    }

    const body = {
      ...formData,
    
      pouso: formData.pouso ? 1 : 0,
    
      km_total: parseFloat(formData.km_total) || 0,
      valor_km: parseFloat(formData.valor_km) || 0,
      valor_dia_pouso: parseFloat(formData.valor_dia_pouso) || 0,
      valor_dia_alimentacao: parseFloat(formData.valor_dia_alimentacao) || 0,
      valor_dia_estacionamento: parseFloat(formData.valor_dia_estacionamento) || 0,
      consumo_medio: parseFloat(formData.consumo_medio) || 0,
      valor_litro_combustivel: parseFloat(formData.valor_litro_combustivel) || 0,
      valor_pedagio: parseFloat(formData.valor_pedagio) || 0,
      perc_garagem: parseFloat(formData.perc_garagem) || 0,
      perc_manutencao: parseFloat(formData.perc_manutencao) || 0,
      perc_folha_salarial: parseFloat(formData.perc_folha_salarial) || 0,
      perc_comissao_motorista: parseFloat(formData.perc_comissao_motorista) || 0,
      caixa_agua: parseFloat(formData.caixa_agua) || 0,
      criado_por: parseInt(formData.criado_por) || 0,
      km_total_realizado: parseFloat(formData.km_total_realizado) || 0,
    
      status_pagamento:
        formData.status_pagamento === "" ? "pendente" : formData.status_pagamento,
    
      status_viagem:
        formData.status_viagem === "" ? "orcamento" : formData.status_viagem,
    
      motorista:
        formData.status_viagem === "reservado"
          ? formData.motorista || ""
          : (formData.status_viagem === "terceiro" ? "" : formData.motorista),
      
      onibus_id:
        formData.status_viagem === "reservado"
          ? formData.onibus_id || ""
          : (formData.status_viagem === "terceiro" ? "" : formData.onibus_id),
      
      nome_terceiro:
        formData.status_viagem === "terceiro"
          ? formData.nome_terceiro || ""
          : (formData.status_viagem === "reservado" ? "" : formData.nome_terceiro),
      
    };

    if (!isFormComplete(true)) return;

    try {
      const url = modo === "editar" ? `/api/orcamento/${id}` : '/api/orcamento';
      const method = modo === "editar" ? 'put' : 'post';

      const res = await api3001({
        method,
        url,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: body,
      });

      Swal.fire("Sucesso", "Orçamento salvo com sucesso!", "success");
      if (onSuccess) onSuccess();

    } catch (error) {
      console.error("Erro:", error);
      const message = error.response?.data?.message || "Erro ao enviar o orçamento.";
      Swal.fire("Erro", message, "error");
    }

  };

  const isFormComplete = (receberDados = false) => {

    const camposObrigatorios = {
      cliente_nome: "Cliente",
      origem: "Origem",
      destino: "Destino",
      data_inicio: "Data de início",
      data_pagamento: "Data de pagamento",
      data_sinal: "Data do sinal",
      data_fim: "Data de fim",
      km_total: "Distância total",
      valor_km: "Valor por KM",
      consumo_medio: "Consumo médio",
      valor_litro_combustivel: "Valor do combustível",
      valor_pedagio: "Pedágio",
      perc_garagem: "% Garagem",
      perc_manutencao: "% Manutenção",
      pagador_estacionamento: "Pagador do estacionamento",
      pagador_alimentacao: "Pagador da alimentação"
    };

    const faltando = [];

    Object.keys(camposObrigatorios).forEach((campo) => {

      const valor = formData[campo];

      if (valor === "" || valor === null || valor === undefined) {
        faltando.push(campo);
      }

    });

    setCamposFaltando(faltando);

    if (faltando.length > 0) {

      if (receberDados) {

        Swal.fire({
          icon: "error",
          title: "Campos obrigatórios faltando",
          html: `
            <div style="text-align:left">
            Os seguintes campos precisam ser preenchidos:<br><br>
            <ul style="padding-left:20px">
              ${faltando.map(c => `<li>${camposObrigatorios[c]}</li>`).join("")}
            </ul>
            </div>
          `
        });

      }

      return false;
    }

    return true;
  };

  const formatarDecimal = (valor) => {
    const numero = parseFloat(valor);
    if (isNaN(numero)) return '0,00';
    return numero.toFixed(2).replace('.', ',');
  };

  useEffect(() => {
    isFormComplete();
  }, [formData]);

  return (
    <>
      <Container>
        <Title>Viagem</Title>
        <SectionGroup>
          <Section>
            <SectionTitle>Informações básicas</SectionTitle>
            <FieldGroup>
              <ClientSearch
                token={token}
                value={formData.cliente_nome}
                onChange={(value) => handleChange("cliente_nome", value)}
                onSelect={handleSelectClient}
              />
              <Label>
                Origem {camposFaltando.includes("origem") && <span>*</span>}
              </Label>
              <Input
                placeholder="Origem"
                maxLength={100}
                value={formData.origem}
                onChange={(e) => handleChange("origem", e.target.value)}
              />
              <Label>
                Destino {camposFaltando.includes("destino") && <span>*</span>}
              </Label>
              <Input
                placeholder="Destino"
                maxLength={100}
                value={formData.destino}
                onChange={(e) => handleChange("destino", e.target.value)}
              />
              <Label>
                Data de início {camposFaltando.includes("data_inicio") && <span>*</span>}
              </Label>
              <Input
                type="date"
                value={formData.data_inicio}
                onChange={(e) => handleChange("data_inicio", e.target.value)}
              />
              <Label>
                Data de fim {camposFaltando.includes("data_fim") && <span>*</span>}
              </Label>
              <Input
                type="date"
                value={formData.data_fim}
                onChange={(e) => handleChange("data_fim", e.target.value)}
              />
              <Label>Horário de Saída</Label>
              <Input
                type="time" 
                value={formData.horario_saida}
                onChange={(e) => handleChange("horario_saida", e.target.value)}
              />
              <Label>Horário de Retorno</Label>
              <Input
                type="time"
                value={formData.horario_retorno}
                onChange={(e) => handleChange("horario_retorno", e.target.value)}
              />
              <Label>
                Distância total (KM) {camposFaltando.includes("km_total") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="KM"
                suffix=" km"
                thousandSeparator="."
                decimalSeparator=","
                value={formData.km_total}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 9999999.99}
                onValueChange={({ value }) => handleChange("km_total", value)}
              />
              {formData.status_viagem === "executado" && (
                <><Label>Distância total realizada (KM)</Label>
                <StyledNumeric
                placeholder="KM"
                suffix=" km"
                thousandSeparator="."
                decimalSeparator=","
                value={formData.km_total_realizado}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 9999999.99}
                onValueChange={({ value }) => handleChange("km_total_realizado", value)}
              /></>
              )

              }
              <Label>
                Valor por KM {camposFaltando.includes("valor_km") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="Valor por KM"
                prefix="R$ "
                thousandSeparator="."
                decimalSeparator=","
                value={formData.valor_km}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                onValueChange={({ value }) => handleChange("valor_km", value)}
              />
              <Label>Embarque (KM)</Label>
              <StyledNumeric
                placeholder="KM"
                suffix=" km"
                thousandSeparator="."
                decimalSeparator=","
                value={formData.km_embarque}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 9999999}
                onValueChange={({ value }) => handleChange("km_embarque", value)}
              />
              <Label>Valor diaria</Label>
              <StyledNumeric
                placeholder="Valor diaria"
                suffix=" R$"
                thousandSeparator="."
                decimalSeparator=","
                value={formData.diaria}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                onValueChange={({ value }) => handleChange("diaria", value)}
              />
              <Label>Pouso</Label>

              <Select
                value={formData.pagador_pouso}
                onChange={(e) => handleChange("pagador_pouso", e.target.value)}
              >
                <option value="">Quem paga?</option>
                <option value="cliente">Agência</option>
                <option value="esportiva">Esportiva</option>
              </Select>

              {formData.pagador_pouso === "esportiva" && (
                <StyledNumeric
                  placeholder="Valor dia"
                  prefix="R$ "
                  decimalSeparator=","
                  value={formData.valor_dia_pouso}
                  isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                  onValueChange={({ value }) =>
                    handleChange("valor_dia_pouso", value)
                  }
                />
              )}
              <Label>
                Alimentação {camposFaltando.includes("pagador_alimentacao") && <span>*</span>}
              </Label>
              <Select
                value={formData.pagador_alimentacao}
                onChange={(e) =>
                  handleChange("pagador_alimentacao", e.target.value)
                }
              >
                <option value="">Quem paga?</option>
                <option value="cliente">Agência</option>
                <option value="esportiva">Esportiva</option>
              </Select>

              {formData.pagador_alimentacao === "esportiva" && (
                <StyledNumeric
                  placeholder="Valor dia"
                  prefix="R$ "
                  decimalSeparator=","
                  value={formData.valor_dia_alimentacao}
                  isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                  onValueChange={({ value }) =>
                    handleChange("valor_dia_alimentacao", value)
                  }
                />
              )}

              <Label>
                Estacionamento {camposFaltando.includes("pagador_estacionamento") && <span>*</span>}
              </Label>
              <Select
                value={formData.pagador_estacionamento}
                onChange={(e) =>
                  handleChange("pagador_estacionamento", e.target.value)
                }
              >
                <option value="">Quem paga?</option>
                <option value="cliente">Agência</option>
                <option value="esportiva">Esportiva</option>
              </Select>

              {formData.pagador_estacionamento === "esportiva" && (
                <StyledNumeric
                  placeholder="Valor dia"
                  prefix="R$ "
                  decimalSeparator=","
                  value={formData.valor_dia_estacionamento}
                  isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                  onValueChange={({ value }) =>
                    handleChange("valor_dia_estacionamento", value)
                  }
                />
              )}
              <Label>
                Data do Sinal {camposFaltando.includes("data_sinal") && <span>*</span>}
              </Label>
              <Input
                type="date"
                value={formData.data_sinal}
                onChange={(e) => handleChange("data_sinal", e.target.value)}
              />

              <Label>
                Data de pagamento {camposFaltando.includes("data_pagamento") && <span>*</span>}
              </Label>
              <Input
                type="date"
                value={formData.data_pagamento}
                onChange={(e) => handleChange("data_pagamento", e.target.value)}
              />
              
            </FieldGroup>
          </Section>

          <Section>
            <SectionTitle>Custos operacionais</SectionTitle>
            <FieldGroup>
              <Label>
                Consumo médio do ônibus (km/l) {camposFaltando.includes("consumo_medio") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="Consumo médio"
                suffix=" km/l"
                decimalSeparator=","
                value={formData.consumo_medio}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 999.99}
                onValueChange={({ value }) =>
                  handleChange("consumo_medio", value)
                }
              />
              <Label>
                Valor do litro {camposFaltando.includes("valor_litro_combustivel") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="Valor do litro"
                prefix="R$ "
                decimalSeparator=","
                value={formData.valor_litro_combustivel}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                onValueChange={({ value }) =>
                  handleChange("valor_litro_combustivel", value)
                }
              />
              <Label>
                Pedágio {camposFaltando.includes("valor_pedagio") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="Pedágio"
                prefix="R$ "
                decimalSeparator=","
                value={formData.valor_pedagio}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                onValueChange={({ value }) =>
                  handleChange("valor_pedagio", value)
                }
              />
              <Label>Caixa de água</Label>
              <StyledNumeric
                placeholder="Caixa de água"
                prefix="R$ "
                decimalSeparator=","
                value={formData.caixa_agua}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 99999999.99}
                onValueChange={({ value }) =>
                  handleChange("caixa_agua", value)
                }
              />
              <Label>
                % Garagem {camposFaltando.includes("perc_garagem") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="% Garagem"
                suffix="%"
                decimalSeparator=","
                value={formData.perc_garagem}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 100}
                onValueChange={({ value }) =>
                  handleChange("perc_garagem", value)
                }
              />
              <Label>
                % Manutenção {camposFaltando.includes("perc_manutencao") && <span>*</span>}
              </Label>
              <StyledNumeric
                placeholder="% Manutenção"
                suffix="%"
                decimalSeparator=","
                value={formData.perc_manutencao}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 100}
                onValueChange={({ value }) =>
                  handleChange("perc_manutencao", value)
                }
              />
              <Label>% Folha salarial</Label>
              <StyledNumeric
                placeholder="% Folha Salarial"
                suffix="%"
                decimalSeparator=","
                value={formData.perc_folha_salarial}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 100}
                onValueChange={({ value }) =>
                  handleChange("perc_folha_salarial", value)
                }
              />

              <Label>% Comissão do motorista</Label>
              <StyledNumeric
                placeholder="% Comissão do Motorista"
                suffix="%"
                decimalSeparator=","
                value={formData.perc_comissao_motorista}
                isAllowed={({ floatValue }) => !floatValue || floatValue <= 100}
                onValueChange={({ value }) =>
                  handleChange("perc_comissao_motorista", value)
                }
              />

              <Label>Status do Pagamento</Label>
              <Select
                value={formData.status_pagamento}
                onChange={(e) => handleChange("status_pagamento", e.target.value)}
              >
                <option value="pendente">Pendente</option>
                <option value="pago">Pago</option>
              </Select>

              <Label>Status da Viagem</Label>
              <Select
                value={formData.status_viagem}
                onChange={(e) => {
                  const status = e.target.value;
                  handleChange("status_viagem", status);
                }}
              >
                <option value="orcamento">Orçamento</option>
                <option value="prereservado">Pré-Reservado</option>
                <option value="reservado">Reservado</option>
                <option value="terceiro">Terceiro</option>
                <option value="cancelado">Cancelado</option>
                <option value="executado">Executado</option>
              </Select>

              {formData.status_viagem === "reservado" && (
                <>
                  <Label>Motorista</Label>
                  <Input
                    placeholder="Motorista"
                    value={formData.motorista || ""}
                    maxLength={100}
                    onChange={(e) => handleChange("motorista", e.target.value)}
                  />

                  <Label>Ônibus</Label>

                  <Select
                    value={formData.onibus_id}
                    onChange={(e) => handleChange("onibus_id", e.target.value)}
                  >

                    <option value="">Selecione um ônibus</option>

                    {onibusLista.map((o) => (

                      <option key={o.id} value={o.id}>
                        {o.nome} / {o.placa} / {o.prefixo}
                      </option>

                    ))}

                  </Select>
                </>
              )}

              {formData.status_viagem === "terceiro" && (
                <>
                  <Label>Nome da Empresa Terceira</Label>
                  <Input
                    placeholder="Nome da Empresa"
                    value={formData.nome_terceiro || ""}
                    maxLength={100}
                    onChange={(e) => handleChange("nome_terceiro", e.target.value)}
                  />
                </>
              )}
              
              

            </FieldGroup>

            <Calculations>
              <SectionTitle>Totais</SectionTitle>
              <CalcRow>
                <CalcLabel>Valor orçado</CalcLabel>
                <CalcValue key={totais.valorOrcado}>R$ {formatarDecimal(totais.valorOrcado)}</CalcValue>
              </CalcRow>
              <CalcRow>
                <CalcLabel>Custo total</CalcLabel>
                <CalcValue key={totais.custoTotal}>R$ {formatarDecimal(totais.custoTotal)}</CalcValue>
              </CalcRow>
              <CalcRow>
                <CalcLabel>Receita</CalcLabel>
                <CalcValue key={totais.receita}>R$ {formatarDecimal(totais.receita)}</CalcValue>
              </CalcRow>
              <CalcRow>
                <CalcLabel>Margem</CalcLabel>
                <CalcValue key={totais.margem}>{formatarDecimal(totais.margem * 100)}%</CalcValue>
              </CalcRow>
            </Calculations>
          </Section>
        </SectionGroup>
      </Container>

      <ButtonBar>
        <Button primary onClick={handleSubmit}>
          Salvar
        </Button>

        {camposFaltando.length === 0 && (
          <>
            <DownloadButton data={{
              ...formData,
              valorOrcado: totais.valorOrcado,
              custoTotal: totais.custoTotal,
              receita: totais.receita,
              margem: totais.margem,
              criado_em,
            }}>Salvar</DownloadButton>
            <PrintButton data={{
              ...formData,
              valorOrcado: totais.valorOrcado,
              custoTotal: totais.custoTotal,
              receita: totais.receita,
              margem: totais.margem,
              criado_em,
            }}>Imprimir</PrintButton>
          </>
        )}
      </ButtonBar>
    </>
  );
};

export default FormOrcamento;
